import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal", "step1", "step2", "step3",
    "message", "codeInput", "requestBtn", "submitBtn",
    "loading", "loadingText", "error", "errorText", "successMessage"
  ]

  connect() {
    // Audit: NO auto-open on page load. The plan's kill list retired the
    // dismissable auto-popup (it trained the operator to dismiss red SubmitHub
    // warnings daily); the undismissable health strip carries the state. The
    // modal now opens only on an explicit verify action via the event below.
    // (checkShouldShowModal remains, called only from explicit triggers.)

    // Listen for external requests to show the modal (from the health strip /
    // playlists refusal handler).
    this.boundShowFromExternal = this.showFromExternal.bind(this)
    document.addEventListener('submithub:show-verification', this.boundShowFromExternal)
  }

  disconnect() {
    document.removeEventListener('submithub:show-verification', this.boundShowFromExternal)
  }

  showFromExternal(event) {
    if (event.detail?.message) {
      this.messageTarget.textContent = event.detail.message
    }
    this.show()
  }

  async checkShouldShowModal() {
    try {
      const response = await fetch('/sp/submithub/should_show_modal', {
        headers: { 'Accept': 'application/json' }
      })
      const data = await response.json()

      if (data.show) {
        if (data.pending_count > 0) {
          this.messageTarget.textContent = `${data.pending_count} playlists are waiting for bot verification. Click below to receive a verification code.`
        }
        this.show()
      }
    } catch (error) {
      console.error('Error checking SubmitHub modal status:', error)
    }
  }

  show() {
    // Always reset to step 1 when opening the modal (prevents showing stale step 3 "success" state)
    this.showStep(1)
    this.hideLoading()
    this.requestBtnTarget.disabled = false
    if (this.hasCodeInputTarget) this.codeInputTarget.value = ''
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false
    this.modalTarget.style.display = 'flex'
    document.body.style.overflow = 'hidden'
  }

  hide() {
    this.modalTarget.style.display = 'none'
    document.body.style.overflow = ''
  }

  close() {
    this.hide()
    // Refresh the page to update playlist statuses after verification
    window.location.reload()
  }

  async dismiss() {
    try {
      await fetch('/sp/submithub/dismiss', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
    } catch (error) {
      console.error('Error dismissing modal:', error)
    }
    this.hide()
  }

  showStep(stepNum) {
    this.step1Target.style.display = stepNum === 1 ? 'block' : 'none'
    this.step2Target.style.display = stepNum === 2 ? 'block' : 'none'
    this.step3Target.style.display = stepNum === 3 ? 'block' : 'none'
    this.hideError()
  }

  backToStep1() {
    this.showStep(1)
  }

  showLoading(text = 'Processing...') {
    this.loadingTarget.style.display = 'flex'
    this.loadingTextTarget.textContent = text
  }

  hideLoading() {
    this.loadingTarget.style.display = 'none'
  }

  showError(message) {
    this.errorTarget.style.display = 'block'
    this.errorTextTarget.textContent = message
  }

  hideError() {
    this.errorTarget.style.display = 'none'
  }

  async requestCode() {
    this.hideError()
    this.requestBtnTarget.disabled = true
    this.showLoading('Sending verification code...')

    try {
      const response = await fetch('/sp/submithub/request_code', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      const data = await response.json()

      if (data.success) {
        this.runId = data.run_id
        // Poll for code_sent status
        this.pollForCodeSent()
      } else {
        this.hideLoading()
        this.showError(data.error || 'Failed to request verification code')
        this.requestBtnTarget.disabled = false
      }
    } catch (error) {
      this.hideLoading()
      this.showError('Network error. Please try again.')
      this.requestBtnTarget.disabled = false
    }
  }

  async pollForCodeSent() {
    let attempts = 0
    const maxAttempts = 30 // 30 * 2s = 60s max

    const poll = async () => {
      attempts++

      try {
        const response = await fetch(`/sp/submithub/check_verification?run_id=${this.runId}`, {
          headers: { 'Accept': 'application/json' }
        })
        const data = await response.json()

        if (data.complete) {
          this.hideLoading()
          this.requestBtnTarget.disabled = false

          if (data.already_valid) {
            // Session already valid, no code needed - show success
            this.successMessageTarget.textContent = 'Your SubmitHub session is already active! Bot checking is enabled.'
            this.showStep(3)
          } else if (data.needs_code) {
            // Code was sent to email, show code input step
            this.showStep(2)
            this.codeInputTarget.focus()
          } else if (data.success && !data.needs_code) {
            // Verification successful (shouldn't happen in this flow, but handle it)
            this.successMessageTarget.textContent = data.message || 'Verification successful!'
            this.showStep(3)
          } else {
            this.showError(data.message || 'Failed to send verification code')
          }
        } else if (attempts < maxAttempts) {
          setTimeout(poll, 2000)
        } else {
          this.hideLoading()
          this.requestBtnTarget.disabled = false
          // Assume code was sent if we timeout
          this.showStep(2)
          this.codeInputTarget.focus()
        }
      } catch (error) {
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000)
        } else {
          this.hideLoading()
          this.requestBtnTarget.disabled = false
          this.showError('Timeout waiting for response. Please try again.')
        }
      }
    }

    poll()
  }

  onCodeInput(event) {
    // Only allow digits
    event.target.value = event.target.value.replace(/\D/g, '').slice(0, 6)
  }

  onCodeKeypress(event) {
    if (event.key === 'Enter' && this.codeInputTarget.value.length === 6) {
      this.submitCode()
    }
  }

  async submitCode() {
    const code = this.codeInputTarget.value.trim()

    if (code.length !== 6) {
      this.showError('Please enter a 6-digit code')
      return
    }

    this.hideError()
    this.submitBtnTarget.disabled = true
    this.showLoading('Verifying code...')

    try {
      const response = await fetch('/sp/submithub/verify_code', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ code: code })
      })

      const data = await response.json()

      if (data.success) {
        this.runId = data.run_id
        this.pollForVerificationResult()
      } else {
        this.hideLoading()
        this.showError(data.error || 'Failed to verify code')
        this.submitBtnTarget.disabled = false
      }
    } catch (error) {
      this.hideLoading()
      this.showError('Network error. Please try again.')
      this.submitBtnTarget.disabled = false
    }
  }

  async pollForVerificationResult() {
    let attempts = 0
    const maxAttempts = 30

    const poll = async () => {
      attempts++

      try {
        const response = await fetch(`/sp/submithub/check_verification?run_id=${this.runId}`, {
          headers: { 'Accept': 'application/json' }
        })
        const data = await response.json()

        if (data.complete) {
          this.hideLoading()
          this.submitBtnTarget.disabled = false

          if (data.success) {
            let message = 'Verification successful!'
            if (data.pending_count && data.pending_count > 0) {
              message = `Verification successful! ${data.pending_count} playlists will now be checked for bots.`
            }
            this.successMessageTarget.textContent = message
            this.showStep(3)
          } else {
            this.showError(data.message || 'Verification failed. The code may be invalid or expired.')
            this.codeInputTarget.value = ''
            this.codeInputTarget.focus()
          }
        } else if (attempts < maxAttempts) {
          setTimeout(poll, 2000)
        } else {
          this.hideLoading()
          this.submitBtnTarget.disabled = false
          this.showError('Timeout waiting for verification. Please try again.')
        }
      } catch (error) {
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000)
        } else {
          this.hideLoading()
          this.submitBtnTarget.disabled = false
          this.showError('Network error. Please try again.')
        }
      }
    }

    poll()
  }
}
